/* eslint @typescript-eslint/strict-boolean-expressions: off */

import CopyToClipboard from "react-copy-to-clipboard";
import React, { useEffect } from "react";
import { FullDashboard, Owner } from "lib/entries/types";
import { InsertDashboardApiKeyMutation } from "lib/types/graphql";
import { InsertDashboardApiKeyMutationVariables } from "lib/types/graphql";
import { InsertQueryApiKeyMutation } from "lib/types/graphql";
import { InsertQueryApiKeyMutationVariables } from "lib/types/graphql";
import { Session } from "lib/users/types";
import { SessionContext } from "gui/session/session";
import { apolloCore } from "lib/apollo/apollo";
import { dashboardLink } from "lib/links/links";
import { embedLink } from "lib/links/links";
import { gql } from "@apollo/client";
import { queryLink } from "lib/links/links";
import { useErrorBoundary } from "hooks/useErrorBoundary";
import modalStyles from "components/Modal/ModalContent.module.css";
import { Modal } from "components/Modal";
import { Button } from "components/Button/Button";
import { useHasViewPermission } from "lib/permissions/permissions";
import { InputField } from "components/Input/InputField";
import { addToastNotification } from "shared/Toasts/toastNotificationStore";

type Props =
  | {
      type: "dashboard";
      label: string;
      dashboard: FullDashboard;
      owner: Owner;
      disabled?: boolean;
      customActionBtn?: React.ReactElement;
    }
  | {
      type: "query";
      label: string;
      query: number;
      visual?: number;
      disabled?: boolean;
      customActionBtn?: React.ReactElement;
    }
  | {
      type: "embed";
      label: string;
      query: {
        id: number;
        is_private: boolean;
      };
      visual: number;
      owner: Owner;
      disabled?: boolean;
      customActionBtn?: React.ReactElement;
    };

export const ShareButton: React.FC<Props> = (props) => {
  return (
    <Modal
      size="L"
      label={props.label}
      content={({ close }) => <ShareDialog {...props} onDismiss={close} />}
      trigger={({ onClick }) =>
        props.customActionBtn ? (
          React.cloneElement(props.customActionBtn, { onClick })
        ) : (
          <Button
            disabled={props.disabled}
            size="M"
            theme="tertiary"
            onClick={onClick}
          >
            {props.label}
          </Button>
        )
      }
    />
  );
};

const ShareDialog: React.FC<
  Props & {
    onDismiss: () => void;
  }
> = (props) => {
  const { session, sessionLoading } = React.useContext(SessionContext);
  const [copied, setCopied] = React.useState(false);
  const [link, setLink] = React.useState("");
  const setError = useErrorBoundary();
  const onCopy = setCopied.bind(null, true);

  const hasViewPermission = useHasViewPermission({
    type: "owner" in props ? props.owner.type : "user",
    id: "owner" in props ? props.owner.id : -1,
  });

  useEffect(() => {
    if (!sessionLoading && !link) {
      createLink({ ...props, hasViewPermission }, session)
        .then(setLink)
        .catch(setError);
    }
  }, [session, sessionLoading, link]);

  useEffect(() => {
    if (copied) {
      addToastNotification({
        level: "success",
        title: "Link copied",
      });
      props.onDismiss();
    }
  }, [copied]);

  const inputLabel = props.type === "dashboard" ? "Dashboard url" : "Query url";

  return (
    <div className={modalStyles.body}>
      <div className={modalStyles.header}>
        <div className={modalStyles.titleRow}>
          <h2>{createTitleText(props)}</h2>
        </div>
      </div>
      <div className={modalStyles.content}>
        <InputField
          size="L"
          style="contained"
          label={inputLabel}
          value={link}
          readOnly
          placeholder=""
          onChange={() => {}}
        />
      </div>
      <div className={modalStyles.buttons}>
        <CopyToClipboard text={link} onCopy={onCopy}>
          <Button size="M" theme="primary">
            Copy link
          </Button>
        </CopyToClipboard>
        <Button onClick={props.onDismiss} size="M" theme="tertiary">
          Close
        </Button>
      </div>
    </div>
  );
};

const createTitleText = (props: Props) => {
  if (props.type === "dashboard") {
    return props.dashboard!.is_private
      ? "Use the following link to share this private dashboard"
      : "Use the following link to share this public dashboard";
  }

  if (props.type === "query") {
    return "Use the following link to share this public query";
  }

  if (props.type === "embed" && props.query.is_private) {
    return "Use the following link to share or embed this private visualization";
  }

  return "Use the following link to share or embed this visualization";
};

const createLink = async (
  props: Props & { hasViewPermission: boolean },
  session?: Session
): Promise<string> => {
  if (props.type === "dashboard") {
    return session && props.dashboard!.is_private && props.hasViewPermission
      ? createPrivateDashboardLink(props.dashboard, session, props.owner)
      : dashboardLink(props.dashboard.owner.handle, props.dashboard.slug);
  }

  if (props.type === "query") {
    return queryLink(props.query, props.visual);
  }

  if (props.type === "embed") {
    return session && props.query.is_private && props.hasViewPermission
      ? createEmbedLink(props.query.id, props.visual, session, props.owner)
      : embedLink(props.query.id, props.visual);
  }

  throw new Error("unknown share dialog type");
};

const createPrivateDashboardLink = async (
  dashboard: FullDashboard,
  session: Session,
  owner: Owner
): Promise<string> => {
  const key = await callInsertDashboardApiKey(dashboard.id, session, owner);
  return dashboardLink(owner.handle, dashboard.slug, key);
};

const createEmbedLink = async (
  query: number,
  visual: number,
  session: Session,
  owner: Owner
): Promise<string> => {
  const key = await callInsertQueryApiKey(query, session, owner);
  return embedLink(query, visual, key);
};

const callInsertDashboardApiKey = async (
  dashboard: number,
  session: Session,
  owner: Owner
): Promise<string> => {
  const res = await apolloCore.mutate<
    InsertDashboardApiKeyMutation,
    InsertDashboardApiKeyMutationVariables
  >({
    mutation: insertDashboardApiKey,
    variables: {
      dashboard_id: dashboard,
      user_id: owner.type === "user" ? owner.id : null,
      team_id: owner.type === "team" ? owner.id : null,
    },
    context: { session },
    fetchPolicy: "no-cache",
  });

  if (!res.data?.insert_api_keys_one?.id) {
    throw new Error("could not create dashboard key");
  }

  return res.data.insert_api_keys_one.id;
};

const callInsertQueryApiKey = async (
  query: number,
  session: Session,
  owner: Owner
): Promise<string> => {
  const res = await apolloCore.mutate<
    InsertQueryApiKeyMutation,
    InsertQueryApiKeyMutationVariables
  >({
    mutation: insertQueryApiKey,
    variables: {
      query_id: query,
      user_id: owner.type === "user" ? owner.id : null,
      team_id: owner.type === "team" ? owner.id : null,
    },
    context: { session },
    fetchPolicy: "no-cache",
  });

  if (!res.data?.insert_api_keys_one?.id) {
    throw new Error("could not create query key");
  }

  return res.data.insert_api_keys_one.id;
};

const insertDashboardApiKey = gql`
  mutation InsertDashboardApiKey(
    $dashboard_id: Int!
    $user_id: Int
    $team_id: Int
  ) {
    insert_api_keys_one(
      object: {
        dashboard_id: $dashboard_id
        user_id: $user_id
        team_id: $team_id
      }
    ) {
      id
    }
  }
`;

const insertQueryApiKey = gql`
  mutation InsertQueryApiKey($query_id: Int!, $user_id: Int, $team_id: Int) {
    insert_api_keys_one(
      object: { query_id: $query_id, user_id: $user_id, team_id: $team_id }
    ) {
      id
    }
  }
`;
